import { useEffect, useState } from "react";
import { Flag, Loader2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Auto-scrolling marquee showing the most recently saved Custom Buddies.
 *
 * Privacy-safe: only shows {name, image} for non-hidden buddies. No email,
 * no IP, no parent identity. Tap-to-report flag on each card hides the
 * buddy from the gallery instantly while admin reviews.
 *
 * Props:
 *   limit?: number    (default 24)
 *   headline?: string
 *   subline?: string
 *   theme?: "cream" | "purple"  — "purple" renders on a dark purple→ink
 *                                 gradient (used on the homepage directly
 *                                 under the hero). "cream" is default.
 */
export default function RecentBuddiesMarquee({
  limit = 24,
  headline = "Real Buddies, made by real families this week",
  subline = "Every Buddy below was dreamed up by a kid. Tag yours @plate_buddies — we might feature it next.",
  theme = "purple",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reporting, setReporting] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/buddies/recent`, { params: { limit } });
        if (!cancelled) setItems(data.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [limit]);

  const handleReport = async (buddyId, reason) => {
    try {
      await api.post(`/buddies/${buddyId}/report`, { reason: reason || null });
      setItems((prev) => prev.filter((b) => b.id !== buddyId));
      toast.success("Thanks — we've hidden that Buddy instantly.");
    } catch {
      toast.error("Couldn't submit report. Please try again.");
    } finally {
      setReporting(null);
    }
  };

  const isPurple = theme === "purple";

  if (loading) {
    return (
      <section className={`py-16 ${isPurple ? "bg-gradient-to-br from-pb-brand-purple via-[#3A1F6B] to-pb-brand-ink" : "bg-pb-cream"}`}>
        <div className={`max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-center gap-2 ${isPurple ? "text-white/70" : "text-pb-soft"}`}>
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm font-bold">Loading recent Buddies…</span>
        </div>
      </section>
    );
  }

  if (items.length === 0) return null;

  // Duplicate items so the CSS marquee loops seamlessly (we render twice and
  // animate the strip by -50% of its width).
  const looped = [...items, ...items];

  return (
    <section
      data-testid="recent-buddies-section"
      className={`relative py-14 sm:py-20 overflow-hidden ${isPurple ? "bg-gradient-to-br from-pb-brand-purple via-[#3A1F6B] to-pb-brand-ink" : "bg-pb-cream"}`}
    >
      {/* Subtle Buddy Teal glow top-right on the purple theme. */}
      {isPurple && (
        <div
          aria-hidden
          className="absolute -top-20 -right-20 w-[460px] h-[460px] rounded-full opacity-20 pointer-events-none"
          style={{ background: "radial-gradient(circle, #06A5A1 0%, transparent 65%)", filter: "blur(40px)" }}
        />
      )}

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 mb-8 text-center sm:text-left">
        <p className={`text-[11px] font-extrabold uppercase tracking-widest inline-flex items-center gap-1.5 ${isPurple ? "text-pb-brand-teal" : "text-pb-coral"}`}>
          <Sparkles className="h-3 w-3" /> Live · Updated every few minutes
        </p>
        <h2
          data-testid="recent-buddies-heading"
          className={`font-display font-black mt-3 text-3xl sm:text-4xl md:text-5xl leading-[0.95] tracking-tight ${isPurple ? "text-white" : "text-pb-ink"}`}
        >
          {headline}
        </h2>
        <p className={`mt-3 text-sm sm:text-base max-w-2xl ${isPurple ? "text-white/70" : "text-pb-soft"}`}>
          {subline}
        </p>
      </div>

      <div className="relative">
        {/* Soft gradient edges so cards fade in/out instead of being chopped.
            Fade color must match the surface — cream on cream, purple on purple. */}
        {isPurple ? (
          <>
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-pb-brand-purple to-transparent" />
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-pb-brand-ink to-transparent" />
          </>
        ) : (
          <>
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-pb-cream to-transparent" />
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-pb-cream to-transparent" />
          </>
        )}

        <div className="recent-buddies-track" data-testid="recent-buddies-marquee">
          {looped.map((b, idx) => (
            <BuddyCard
              key={`${b.id}-${idx}`}
              buddy={b}
              onAskReport={() => setReporting(b)}
            />
          ))}
        </div>
      </div>

      {reporting && (
        <ReportModal
          buddy={reporting}
          onClose={() => setReporting(null)}
          onConfirm={(reason) => handleReport(reporting.id, reason)}
        />
      )}

      <style>{`
        .recent-buddies-track {
          display: flex;
          gap: 1.25rem;
          width: max-content;
          padding: 0 2rem;
          animation: recent-buddies-scroll 60s linear infinite;
        }
        .recent-buddies-track:hover {
          animation-play-state: paused;
        }
        @keyframes recent-buddies-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .recent-buddies-track {
            animation: none;
            overflow-x: auto;
          }
        }
      `}</style>
    </section>
  );
}

function BuddyCard({ buddy, onAskReport }) {
  const imageSrc = buddy.image_url?.startsWith("/") ? `${BACKEND_URL}${buddy.image_url}` : buddy.image_url;
  return (
    <div
      data-testid={`recent-buddy-card-${buddy.id}`}
      className="group relative w-44 sm:w-52 flex-shrink-0 rounded-[1.75rem] bg-white border-2 border-white shadow-[0_14px_32px_rgba(28,22,40,0.08)] overflow-hidden hover:-translate-y-1 transition"
    >
      <div className="aspect-square bg-pb-cream overflow-hidden">
        <img
          src={imageSrc}
          alt={buddy.name}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="px-4 py-3 text-center">
        <h3 className="font-display font-black text-pb-ink text-lg leading-tight truncate">
          {buddy.name}
        </h3>
      </div>
      <button
        type="button"
        onClick={onAskReport}
        aria-label={`Report ${buddy.name}`}
        data-testid={`btn-report-${buddy.id}`}
        className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/80 backdrop-blur-sm text-pb-soft hover:text-pb-coral hover:bg-white shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
      >
        <Flag className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ReportModal({ buddy, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-testid="report-buddy-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-pb-ink/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-[2rem] shadow-[0_30px_80px_rgba(28,22,40,0.35)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white text-pb-ink border border-pb-sky/30 hover:text-pb-coral flex items-center justify-center"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="px-6 sm:px-8 pt-7 pb-4">
          <p className="text-[11px] font-extrabold uppercase tracking-widest text-pb-coral">
            Report this Buddy
          </p>
          <h2 className="font-display font-black text-2xl text-pb-ink mt-2 leading-tight">
            Something off about {buddy.name}?
          </h2>
          <p className="text-sm text-pb-soft mt-2 leading-snug">
            We&apos;ll hide it from the gallery instantly while we review. Anything we should know?
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="input-report-reason"
            placeholder="Optional — what felt wrong? (e.g. inappropriate, scary, looks broken…)"
            rows={3}
            maxLength={300}
            className="mt-4 w-full rounded-2xl border-2 border-pb-sky/40 bg-pb-cream px-4 py-3 text-sm text-pb-ink placeholder:text-pb-soft/60 focus:outline-none focus:border-pb-coral focus:bg-white transition resize-none"
          />
        </div>
        <div className="px-6 sm:px-8 pb-7 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 inline-flex items-center justify-center rounded-full bg-white text-pb-ink font-bold px-5 py-3 border-2 border-pb-sky/40 hover:border-pb-coral transition"
          >
            Cancel
          </button>
          <button
            data-testid="btn-confirm-report"
            disabled={submitting}
            onClick={async () => {
              setSubmitting(true);
              await onConfirm(reason);
            }}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-5 py-3 hover:scale-105 transition shadow-[0_10px_24px_rgba(242,106,52,0.35)] disabled:opacity-60 disabled:hover:scale-100"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flag className="h-4 w-4" />}
            Hide it now
          </button>
        </div>
      </div>
    </div>
  );
}
